import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Patient} from "../model/patient.";
import {EMPTY, forkJoin, from} from "rxjs";
import {AppConfig} from "../shared/constants/app.config";
import {expand, finalize, map, mergeMap, reduce} from "rxjs/operators";
import {Sort} from "@angular/material/sort";
import { HttpClient } from "@angular/common/http";
import {UtilityService} from "../shared/service/utility.service";
import {SpinnerService} from "../spinner/spinner.service";

import {AngularAppLogger} from "../angular-app-logger";
import {ObservationVitalSignsService} from "./observation.vital-signs.service";

@Component({
    selector: 'app-observation.vital-signs',
    templateUrl: './observation.vital-signs.component.html',
    styleUrl: './observation.vital-signs.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ObservationVitalSignsComponent implements OnInit {
  vitalsigns: any;
  now: Date;
  startDate: Date;
  endDate: Date;
  selectedNewEntries: any[] = [];
  selectedUpdatedEntries: any[] = [];
  selectedNewResources: any[] = [];
  selectedUpdatedResources: any[] = [];
  psScope: string;
  ehrpatient: Patient;
  isAllSelected: boolean;
  isAlldisabled: boolean;
  totalSuccessCount: number;
  totalFailCount: number;
  categoryData: any = [];
  selectedOrg: any;
  selectedVital: string;
  selectedDate: string;
  // Filter criteria
  vitalsignOptions: string[] ;
  dateOptions: string[] ;

  @ViewChild('vitalSelector') vitalSelector;
  @ViewChild('dateSelector') dateSelector;
  constructor(
    readonly httpClient: HttpClient,
    public observationVitalSignsService: ObservationVitalSignsService,
    readonly utility: UtilityService,
    readonly spinner: SpinnerService,
    readonly appLogger: AngularAppLogger
  ) {
    const data = utility.data;
    if (data.vitalsigns) {
      this.vitalsigns = this.utility.bundleResources(data.vitalsigns).entry;
      this.appLogger.logInfo(`${this.vitalsigns.length} vital-sign observations fetched from EHR`, { context: `Logged from Obs Vitals Component`, allowDuplicate: true });
    }
    this.selectedOrg = data.selectedOrg;
    this.psScope = this.selectedOrg.value;
  }

  ngOnInit()
  {
    this.categoryData = this.vitalsigns;
    this.sortHeader({ active: "time", direction: "desc" });
    this.now = new Date();
    this.startDate = this.utility.data.startDate ?? new Date(new Date().setFullYear(new Date().getFullYear() - 2));
    this.endDate = this.utility.data.endDate ?? new Date();

    const telecomUrlItems = this.selectedOrg.telecomUrlItems;

    if (telecomUrlItems && telecomUrlItems.length > 0) {
      const urls = telecomUrlItems.map((item) => item.value);
      forkJoin(urls.map((url) => this.getCibmtrVitalSigns(url)));
    } else {
      this.getCibmtrVitalSigns(localStorage.getItem("iss"));
    }
    // Filter criteria initialized
    this.vitalsignOptions = Array.from(
      new Set(this.categoryData.map((item) => item.resource?.code?.text))
    );
    this.dateOptions = Array.from(
      new Set(this.categoryData.map((item) => item.resource?.effectiveDateTime))
    );
    this.selectedVital = '';
    this.selectedDate = '';
  }

  getCibmtrVitalSigns(url: string)
  {
    this.spinner.start();
    this.observationVitalSignsService
      .getCibmtrObservationsVitalSigns(this.selectedOrg.value, AppConfig.CIBMTR_FHIR_R4_URL)
      .pipe(
        expand((response) => {
          const next =
            response.link && response.link.find((l: any) => l.relation === "next");
          if (next) {
            const modifiedUrl =
              `${AppConfig.CIBMTR_FHIR_R4_URL}?${next.url.split("?")[1]}`;
            return this.httpClient.get(modifiedUrl);
          } else {
            return EMPTY;
          }
        }),
        map((response: any) => {
          if (response.entry) {
            return response.entry.flatMap((array) => array);
          }
          return [];
        }),
        reduce((acc, x) => acc.concat(x), [])
      )
      .subscribe(
        (savedEntries) => {
          this.updateObservationEntries(url, savedEntries);
          if (savedEntries.length > 0) {
            this.appLogger.logInfo(`${savedEntries.length} previously saved Vital-signs found`, {
              context: 'Retrieval of saved CIBMTR Vital-signs',
              url: `${url}`,
              allowDuplicate: true
            });
          }
          this.spinner.end();
        },
        (error) => {
          this.spinner.end();
          let errorMsg = "Error occurred while fetching saved vital sign observations. Check message tray for details.";
          alert(errorMsg);
          this.appLogger.logError(errorMsg, {
            url: AppConfig.CIBMTR_FHIR_R4_URL,
            crid: this.utility.data.crid,
            startDate: this.utility.data.startDate,
            endDate: this.utility.data.endDate
          });
          this.appLogger.logError(error, { context: 'Fetch saved vital signs observations from Cibmtr' });
        }
      );
  }

  private updateObservationEntries(url: any, savedEntries: any[]) {
    const entries = this.categoryData;
    if (entries?.length > 0) {
      //Filters Operationoutcome entries
      const observationEntries = entries.filter(function (item: any) {
        return item.resource.resourceType === "Observation";
      });

      for (const observationEntry of observationEntries) {
        this.updateObsSubjReference(observationEntry, url);
        // Case I - Default state the record has not been submitted
        if (!observationEntry.state) {
          observationEntry.state = "bold";
          observationEntry.selected = false;
        }

        if (savedEntries?.length > 0) {
          // Case II - The record has been submitted and there were no updates
          // we cannot submit these records unless there is a change in data.
          // sse stands for submitted  saved entry
          const sse = this.populateSubmittedSavedEntries(savedEntries, observationEntry);

          // use - updated saved entry
          const use = this.populateUpdatedSavedEntries(savedEntries, observationEntry);

          if (sse.length > 0) {
            observationEntry.selected = true;
            observationEntry.state = "lighter";
            observationEntry.resource.id = sse[0].resource.id;
          }

          // Case III - The record has been submitted and there were updates made after
          else if (use.length > 0) {
            observationEntry.state = "normal";
            observationEntry.resource.id = use[0].resource.id;
          }
        }
      }
    }
  }

  private populateSubmittedSavedEntries(savedEntries: any[], observationEntry: any) {
    return savedEntries.filter((savedEntry) => {
      const createdId = savedEntry.resource.identifier[0].value.match(
        new RegExp("/Observation/(.*)")
      )[1];

      return (
        savedEntry.resource.identifier &&
        observationEntry.resource.id === createdId &&
        observationEntry.resource.issued === savedEntry.resource.issued
      );
    });
  }

  private populateUpdatedSavedEntries(savedEntries: any[], observationEntry: any) {
    return savedEntries.filter((savedEntry) => {
      const updatedId = savedEntry.resource.identifier[0].value.match(
        new RegExp("/Observation/(.*)")
      )[1];

      return (
        savedEntry.resource.identifier &&
        observationEntry.resource.id === updatedId &&
        observationEntry.resource.issued !== savedEntry.resource.issued
      );
    });
  }


  getNodeValue(value: any) {
    if (value) {
      if (this.isValueCodeableConcept(value)) {
        return value.valueCodeableConcept.coding[0].code;
      } else if (this.isValueQuantity(value)) {
        return this.getValueQuantityUnitOrValue(value.valueQuantity);
      } else if (value.valueDateTime) {
        return value.valueDateTime;
      } else if (value.valueString) {
        return value.valueString;
      } else if (value.valueTime) {
        return value.valueTime;
      } else if (value.valueBoolean) {
        return value.valueBoolean;
      }
    }
    return null;
  }

  getValueQuantityUnitOrValue(valueQuantity: any) {
    if (valueQuantity.value && valueQuantity.unit) {
      return `${valueQuantity.value} ${valueQuantity.unit}`;
    } else if (valueQuantity.value) {
      return valueQuantity.value;
    } else {
      return null;
    }
  }

  isValueQuantity(value: any) {
    return value.valueQuantity &&
      (value.valueQuantity.value || value.valueQuantity.unit);
  }

  isValueCodeableConcept(value: any) {
    return value.valueCodeableConcept &&
      value.valueCodeableConcept.coding[0] &&
      value.valueCodeableConcept.coding[0].code;
  }

  updateObsSubjReference(observation: any, url: string) {
    if (observation?.fullUrl && url && observation?.resource?.subject?.reference) {
      if (observation.fullUrl.toString().includes(url.toString()) && !(observation.resource.subject.reference.toString().includes('http'))) {
        observation.resource.subject.reference = `${url}/${observation.resource.subject.reference}`;
      }
    }
  }

  //TODO: Check when refactoring in future if this method is actually needed or not
  getComponentValue(component: any) {
    if (component) {
      const components = [];
      for (let comp of component) {
        const code = `${comp.code.text}: `;
        const nodeValue = this.getNodeValue(comp);
        components.push(code + nodeValue);
      }
      return components.join(",");
    } else {
      return null;
    }
  }

  submitToCibmtr() {
    //reset
    this.selectedNewEntries = [];
    this.selectedUpdatedEntries = [];
    this.selectedNewResources = [];
    this.selectedUpdatedResources = [];

    // New Records
    this.selectedNewEntries.push(
      this.filterData().filter((m) => m.selected === true && m.state === "bold")
    );

    this.selectedNewResources = Array.prototype.concat.apply(
      [],
      this.selectedNewEntries
    );

    this.selectedNewResources = Array.prototype.concat.apply(
      [],
      this.utility.buildSelectedResources(this.selectedNewResources)
    );

    // Updated Records
    this.selectedUpdatedEntries.push(
      this.filterData().filter(
        (m) => m.selected === true && m.state === "normal"
      )
    );

    this.selectedUpdatedResources = Array.prototype.concat.apply(
      [],
      this.selectedUpdatedEntries
    );

    this.selectedUpdatedResources = Array.prototype.concat.apply(
      [],
      this.utility.buildSelectedResources(this.selectedUpdatedEntries)
    );

    const totalEntries = [
      ...this.selectedNewResources,
      ...this.selectedUpdatedResources,
    ];

    if (totalEntries && totalEntries.length > 0) {
      const bundles = this.observationVitalSignsService.getBundles(
        totalEntries,
        this.psScope
      );

      let _successCount = 0;

      this.spinner.start();
      from(bundles)
        .pipe(
          mergeMap(
            (bundle) => this.observationVitalSignsService.postBundle(bundle),
            8  // limit number of concurrent bundle submissions
          ),
          finalize(() => {
            this.spinner.end();
            this.totalSuccessCount = _successCount;
            this.totalFailCount = totalEntries.length - _successCount;
            this.checkForSelectAll();
            //Send Message Header Bundle only upon successful Observations submission.
            if (this.isAllSelected && this.totalSuccessCount > 0 && this.totalFailCount === 0) {
              this.observationVitalSignsService.submitMsgHeader(this.observationVitalSignsService.getMsgHeaderBundle()).subscribe(
                (response: any) => {
                  this.appLogger.logInfo(response, { context: 'Submitting MsgHeader from Obs vitals component' });
                }
              );
            }
            if (this.totalFailCount > 0) {
              this.appLogger.logError(`Failed to submit ${this.totalFailCount} Obs Vital signs to Cibmtr`, { context: `submitToCibmtr in Obs Vitals component` });
            }
          })
        )
        .subscribe(
          (response: any) => {
            response.entry &&
            response.entry.forEach((entry: any) => {
              const idValue = entry.resource.identifier[0].value;
              const matchedEntry = totalEntries.find((e) => {
                return idValue === e.fullUrl;
              });
              if (matchedEntry) matchedEntry.state = "lighter";
              _successCount++;
            });
            this.appLogger.logInfo(`Successfully submitted ${_successCount} Obs vital signs to Cibmtr`, { context: `submitToCibmtr in Obs vitals component`, allowDuplicate: true });
          },
          (errorBundle) => {
            this.spinner.reset();
            this.appLogger.logError(errorBundle, { context: `Error in Submitting Vital signs to Cibmtr` });
          }
        );
    }
  }

  checkForSelectAll() {
    this.isAllSelected = this.filterData().every((entry) => entry.selected);
    this.isAlldisabled = this.filterData().every(
      (entry) => entry.selected && entry.state === "lighter"
    );
  }

  selectAll() {
    const toggleStatus = this.isAllSelected;
    this.filterData().forEach((entry) => {
      if (entry.state !== "lighter") {
        entry.selected = toggleStatus;
      }
    });
  }

  toggleOption = () => {
    this.isAllSelected = this.categoryData.every((entry: any) => entry.selected);
  };

  toggleAllOption = () => {
    this.isAlldisabled = this.categoryData.every((entry: any) => entry.disabled);
  };

  get toggleAlert() {
    return this.totalSuccessCount > 0 || this.totalFailCount > 0
      ? "show"
      : "hide";
  }

  sortHeader(event: Sort) {
    const isAsc = event.direction === "asc";

    if (!event.active || event.direction === "") {
      return this.categoryData;
    } else {
      this.categoryData.sort((a: any, b: any) => {
        switch (event.active) {
          case "time":
            return this.compare(
              a.resource.effectiveDateTime,
              b.resource.effectiveDateTime,
              isAsc
            );
          case "name":
            return this.compare(
              a.resource.code?.text,
              b.resource.code?.text,
              isAsc
            );
          default:
            return null;
        }
      });
    }
    return null;
  }

  compare(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean
  ) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  filterVitalSign(event: any) {
    this.selectedVital = event.value;
    this.checkForSelectAll();
  }

  filterDate(event: any) {
    this.selectedDate = event.value;
    this.checkForSelectAll();
  }

  filterData(): any [] {
    return this.categoryData.filter((item: any) => {
      const codeMatches = this.selectedVital
        ? item.resource?.code?.text === this.selectedVital
        : true;
      const dateMatches = this.selectedDate
        ? item.resource?.effectiveDateTime === this.selectedDate
        : true;

      return codeMatches && dateMatches;
    });
  }
  clearFilters()
  {
    this.selectedVital = '';
    this.selectedDate = '';
    this.vitalSelector.value = 'Select Vital Sign';
    this.dateSelector.value = 'Select Date/Time';
    this.checkForSelectAll();
  }
}
